import React from 'react';
import { useTranslation } from 'react-i18next';
import { CardTravelRounded, PhoneOutlined } from '@mui/icons-material';
import BroadcastOnPersonalOutlinedIcon from '@mui/icons-material/BroadcastOnPersonalOutlined';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import Hearing from '@mui/icons-material/Hearing';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import CoffeeIcon from 'shared/components/icons/coffee-icon';
import { InBathroomIcon } from 'shared/components/icons/in-bathroom-icon';
import IntruderIcon from 'shared/components/icons/intruder-icon';
import { OpenDoorIcon } from 'shared/components/icons/open-door-icon';
import OutOfBedIcon from 'shared/components/icons/out-of-bed-icon';
import { OutOfRoomIcon } from 'shared/components/icons/out-of-room-icon';
import SolidPersonFallingIcon from 'shared/components/icons/solid-person-falling-icon';
import SolidPersonWalkingDashedLineArrowRightIcon from 'shared/components/icons/solid-person-walking-dashed-line-arrow-right-icon';
import { ScenarioType } from 'shared/enums/scenario-type';

import ResidentChangeLog from 'features/residents/models/resident-change-log';
import { ResidentChangeLogCategory } from 'features/residents/models/resident-change-log-category';
import styles from 'features/residents/resident-change-logs/views/components/columns/category-column/category-column.module.scss';

type Props = {
  item: ResidentChangeLog;
};

const CategoryColumn = ({ item }: Props): React.JSX.Element => {
  const { t } = useTranslation('residentsChangeLog');

  const iconSX = {
    color: Constants.Colors.primary,
    width: 16,
    height: 16,
  };

  const renderScenarioLabel = (): React.JSX.Element | undefined => {
    if (item.scenarioType) {
      return (
        <div className={styles.labelContainer}>
          {OrganizationUnitScenarioIcon(item.scenarioType)}
          <Typography variant="overline">{t(`residentsChangeLogTable.scenarioName.${item.scenarioType}`)}</Typography>
        </div>
      );
    }
  };

  const OrganizationUnitScenarioIcon = (scenarioType: ScenarioType): React.JSX.Element => {
    switch (scenarioType) {
      case ScenarioType.Bed:
        return <OutOfBedIcon sx={iconSX} />;
      case ScenarioType.Room:
        return <OutOfRoomIcon sx={iconSX} />;
      case ScenarioType.Door:
        return <OpenDoorIcon sx={iconSX} />;
      case ScenarioType.Bathroom:
        return <InBathroomIcon sx={iconSX} />;
      case ScenarioType.Intruder:
        return <IntruderIcon sx={iconSX} />;
      case ScenarioType.FallDetection:
        return <SolidPersonFallingIcon sx={iconSX} />;
      case ScenarioType.Audio:
        return <PhoneOutlined sx={iconSX} />;
      case ScenarioType.Video:
        return <VideocamOutlined sx={iconSX} />;
      case ScenarioType.Acoustic:
        return <Hearing sx={iconSX} />;
      case ScenarioType.CurrentLocation:
        return <PinDropOutlined sx={iconSX} />;
      case ScenarioType.WanderingDetection:
        return <SolidPersonWalkingDashedLineArrowRightIcon sx={iconSX} />;
      case ScenarioType.Absence:
        return <CardTravelRounded sx={iconSX} />;
      case ScenarioType.Visitor:
        return <CoffeeIcon sx={iconSX} />;
      default:
        return <></>;
    }
  };

  const renderDeviceLabel = (): React.JSX.Element => (
    <div className={styles.labelContainer}>
      <BroadcastOnPersonalOutlinedIcon sx={iconSX} />
      <Typography variant="overline">{item.deviceName}</Typography>
    </div>
  );

  const renderInformationLabel = (): React.JSX.Element => (
    <div className={styles.labelContainer}>
      <CommentOutlined sx={iconSX} />
      <Typography variant="overline">{t('residentsChangeLogTable.category.Information')}</Typography>
    </div>
  );

  const renderMuteScenariosLabel = (): React.JSX.Element => (
    <div className={styles.labelContainer}>
      {item.field === 'VisitorPresentPeriod' && <CoffeeIcon sx={iconSX} />}
      {item.field === 'AbsencePeriod' && <CardTravelRounded sx={iconSX} />}
      <Typography variant="overline">{t(`residentsChangeLogTable.category.${item.field}InfoLabel`)}</Typography>
    </div>
  );

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1">{t(`residentsChangeLogTable.category.${item.category}`)}</Typography>
      {item.category === ResidentChangeLogCategory.Scenario && item.scenarioType && renderScenarioLabel()}
      {item.category === ResidentChangeLogCategory.Device && item.deviceName && renderDeviceLabel()}
      {item.category === ResidentChangeLogCategory.Information && renderInformationLabel()}
      {item.category === ResidentChangeLogCategory.MuteScenarios && renderMuteScenariosLabel()}
    </div>
  );
};

export default CategoryColumn;
